import React, { useState, useMemo, useEffect } from "react";

import {
  Typography,
  InfoPanel,
  Label,
  DocumentList,
  Button,
  Modal,
  SuccessModalContent,
  TextArea,
  TableMetadata,
  useTable,
  Table,
} from "@king-ict/eupisi-ui/components";
import { useInput } from "@king-ict/eupisi-ui/hooks";
import { DocumentData } from "@king-ict/eupisi-ui/models";
import { nameof, hrDate, hrShortTime } from "@king-ict/eupisi-ui/utils";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams, useHistory, useRouteMatch } from "react-router";

import {
  getPhysicalExaminationData,
  getPhysicalExaminationDocument,
  rejectPhysicalExamination,
  confirmPhysicalExamination,
} from "../../../../api";
import {
  requestsRoute,
  subsequentRequestsRoute,
  requestDetailsRoute,
  subsequentRequestDetailsRoute,
  preschoolRequestsRoute,
} from "../../../../app";
import { MatchParams } from "../../../../components/Sidebar";
import { useBusinessRule } from "../../../../hooks";
import {
  RequestPhysicalExamination,
  PhysicalExaminationStatusEnum,
  UnlockReason,
  RequestHistoryData,
  RequestPersonInfo,
} from "../../../../models";
import styles from "./RequestPhysicalExaminationPage.module.scss";

export interface RequestPhysicalExaminationPageProps {
  requestPersonInfo: RequestPersonInfo;
}

export const RequestPhysicalExaminationPage = ({
  requestPersonInfo,
}: RequestPhysicalExaminationPageProps) => {
  const { requestId } = useParams<{ requestId: string }>();
  const { t } = useTranslation();
  const history = useHistory();
  const requestMatch = useRouteMatch<MatchParams>(requestDetailsRoute);
  const subsequentRequestMatch = useRouteMatch<MatchParams>(
    subsequentRequestDetailsRoute
  );
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [openConfirmSuccessModal, setOpenConfirmSuccessModal] = useState(false);
  const { canUpdatePhysicalExamination } = useBusinessRule();
  const [rejectModalOpen, setRejectModalOpen] = useState<boolean>(false);
  const [rejectSuccessModalOpen, setRejectSuccessModalOpen] = useState<boolean>(
    false
  );
  const { handleChange } = useInput();

  const [rejectReason, setRejectReason] = useState<UnlockReason>(
    {} as UnlockReason
  );
  const [
    requestPhysicalExaminationData,
    setRequestPhysicalExaminationData,
  ] = useState<RequestPhysicalExamination>({
    statusId: PhysicalExaminationStatusEnum.Unknown,
    status: "",
    sentDocuments: [],
    rejectedData: [],
    history: [],
  } as RequestPhysicalExamination);

  const handleToggleConfirmModal = () => {
    setOpenConfirmModal((openConfirmModal) => !openConfirmModal);
  };

  const handleToggleConfirmSuccessModal = () => {
    setOpenConfirmSuccessModal(
      (openConfirmSuccessModal) => !openConfirmSuccessModal
    );
  };

  const handleToggleRejectModalOpen = () => {
    setRejectModalOpen((prevRejectModalOpen) => !prevRejectModalOpen);
  };

  const handleToggleRejectSuccessModalOpen = () => {
    setRejectSuccessModalOpen((prev) => !prev);
  };

  const handlePushToRoute = () => {
    history.push(
      requestMatch?.path === requestDetailsRoute
        ? requestsRoute
        : subsequentRequestMatch?.path === subsequentRequestDetailsRoute
        ? subsequentRequestsRoute
        : preschoolRequestsRoute
    );
  };

  const handleRejectSuccessModalClose = () => {
    handleToggleRejectSuccessModalOpen();
    handlePushToRoute();
  };

  const handleConfirmedSuccessModalClose = () => {
    handleToggleConfirmSuccessModal();
    handlePushToRoute();
  };

  useEffect(() => {
    (async () => {
      if (requestId) {
        const requestPhysicalExaminationTempData = await getPhysicalExaminationData(
          requestId
        );
        if (requestPhysicalExaminationTempData !== null) {
          setRequestPhysicalExaminationData(requestPhysicalExaminationTempData);
        }
      }
    })();
  }, [requestId]);

  const handleDownloadDocument = (document: DocumentData) => {
    if (document.fileID) {
      getPhysicalExaminationDocument(document.fileID);
    }
  };

  const getStatusColor = (statusId: number) => {
    switch (statusId) {
      case PhysicalExaminationStatusEnum.DocumentApproved:
        return "success";
      case PhysicalExaminationStatusEnum.ReadyForEvaluation:
        return "warning";
      case PhysicalExaminationStatusEnum.DocumentRejected:
        return "danger";
      default:
        return "warning";
    }
  };

  const handleConfirmDocument = () => {
    (async () => await confirmPhysicalExamination(+requestId))().then(() => {
      handleToggleConfirmModal();
      handleToggleConfirmSuccessModal();
    });
  };

  const handleRejectConfirmed = () => {
    (async () =>
      await rejectPhysicalExamination(rejectReason, +requestId))().then(() => {
      handleToggleRejectModalOpen();
      handleToggleRejectSuccessModalOpen();
    });
  };

  const getPhysicalExaminationRejectedDataItems = () => {
    return (
      <>
        {requestPhysicalExaminationData.rejectedData.map((reject) => (
          <React.Fragment key={reject.requestCommentId}>
            <DocumentList
              onDocumentClick={handleDownloadDocument}
              color="primary"
              documents={reject.documents}
              fullWidth
              removable={false}
            />
            <Typography variant="small" color="light">
              {`${hrDate(reject.date)} ${hrShortTime(reject.date)}, ${
                reject.user
              }, ${t("request.physicalExamination.reason")}: ${
                reject.requestComment
              }`}
            </Typography>
            <hr className={styles.commentline} />
          </React.Fragment>
        ))}
      </>
    );
  };

  const historyTableMetadata = useMemo(
    () =>
      ({
        rowKey: "id",
        columns: [
          {
            columnKey: "dateColumn",
            accessor: (history) => {
              return (
                <>
                  <Typography variant="base">
                    {`${hrDate(history.date)} ${hrShortTime(history.date)}`}
                  </Typography>
                </>
              );
            },
          },
          {
            columnKey: "statusColumn",
            accessor: (history) => {
              return (
                <>
                  <Typography
                    variant="base"
                    color={getStatusColor(history.statusId)}
                  >
                    {history.status}
                  </Typography>
                </>
              );
            },
          },
          {
            columnKey: "userColumn",
            accessor: (history) => {
              return (
                <>
                  <Typography variant="base">{history.user}</Typography>
                </>
              );
            },
          },
        ],
      } as TableMetadata<RequestHistoryData>),
    [t]
  );

  const [tableProps, _, __] = useTable({
    metadata: historyTableMetadata,
    data: requestPhysicalExaminationData?.history,
    pagination: false,
    totalItems: requestPhysicalExaminationData.history.length,
  });

  return (
    <>
      <Row className={`${styles.rowHeadingSpace}`}>
        <Col xs={12} className={styles.withoutPadding}>
          <InfoPanel
            message={t("request.physicalExamination.infoMessage")}
            color="success"
            fullWidth
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <Typography variant="largeBold">
            {t("sidebar.physicalExamination")}
          </Typography>
        </Col>
      </Row>
      <Row>
        <Col md={4}>
          <Typography
            variant="mediumBold"
            color="base"
            className={styles.spaceClass}
          >
            {t("common.status")}
          </Typography>
          <div className={styles.requestStatus}>
            <Label
              color={getStatusColor(requestPhysicalExaminationData.statusId)}
            >
              {requestPhysicalExaminationData.status
                ? requestPhysicalExaminationData.status
                : t("request.physicalExamination.onHold")}
            </Label>
          </div>
        </Col>
      </Row>
      <Row className={styles.rowHeadingSpace}>
        <Col md={12} className={styles.withoutPadding}>
          <Typography variant="smallHeading" className={styles.spaceClass}>
            {t("common.documentsAdded")}
          </Typography>
          {requestPhysicalExaminationData?.sentDocuments &&
          requestPhysicalExaminationData?.sentDocuments.length > 0 ? (
            <DocumentList
              onDocumentClick={handleDownloadDocument}
              color="primary"
              documents={requestPhysicalExaminationData?.sentDocuments}
              fullWidth
              removable={false}
            />
          ) : (
            <Typography variant="baseBold" color="base">
              {t("common.noDocuments")}
            </Typography>
          )}
        </Col>
      </Row>
      {canUpdatePhysicalExamination(
        requestPersonInfo.requestPedagogicalYearId,
        requestPhysicalExaminationData.statusId,
        requestPersonInfo.enrollmentTypeId,
        requestPersonInfo.monthId
      ) && (
        <Row className={`${styles.withoutPadding} ${styles.buttonsLayout}`}>
          <Col
            md={12}
            className={`${styles.withoutPadding} ${styles.buttonsRight}`}
          >
            <Button
              size="sm"
              color="primary"
              className={styles.buttonSpace}
              outline
              onClick={handleToggleRejectModalOpen}
            >
              {t("request.physicalExamination.rejectDocument")}
            </Button>

            <Button
              size="sm"
              color="primary"
              className={styles.buttonSpace}
              onClick={handleToggleConfirmModal}
            >
              {t("request.physicalExamination.confirmDocument")}
            </Button>
          </Col>
        </Row>
      )}
      <hr className={styles.line} />
      {requestPhysicalExaminationData.statusId && (
        <>
          <Row className={styles.rowHeadingSpace}>
            <Col md={12} className={styles.withoutPadding}>
              <Typography variant="smallHeading" className={styles.spaceClass}>
                {t("request.physicalExamination.documentsRejected")}
              </Typography>
              {requestPhysicalExaminationData?.rejectedData &&
              requestPhysicalExaminationData?.rejectedData.length > 0 ? (
                getPhysicalExaminationRejectedDataItems()
              ) : (
                <Typography variant="baseBold" color="base">
                  {t("common.noDocuments")}
                </Typography>
              )}
            </Col>
          </Row>
          <Row className={styles.rowHeadingSpace}>
            <Col md={12} className={styles.withoutPadding}>
              <Typography variant="smallHeading" className={styles.spaceClass}>
                {t("request.physicalExamination.events")}
              </Typography>
              <Table {...tableProps} />
            </Col>
          </Row>
        </>
      )}

      <Modal
        type={"confirm"}
        title={t("request.physicalExamination.confirmDocumentTitle")}
        open={openConfirmModal}
        confirmText={t("auth.submit")}
        cancelText={t("common.no")}
        onClose={handleToggleConfirmModal}
        onConfirm={handleConfirmDocument}
      ></Modal>

      <Modal
        type={"message"}
        open={openConfirmSuccessModal}
        confirmText={t("common.close")}
        onConfirm={handleConfirmedSuccessModalClose}
        onClose={() => handleConfirmedSuccessModalClose()}
      >
        <SuccessModalContent
          message={t(
            "request.physicalExamination.confirmDocumentSuccesfullMessage"
          )}
        />
      </Modal>

      <Modal
        open={rejectModalOpen}
        onClose={handleToggleRejectModalOpen}
        title={t("request.physicalExamination.rejectDocumentTitle")}
        showCloseMark={false}
      >
        <TextArea
          type="text"
          label={t("request.physicalExamination.rejectReasonLabel")}
          ariaLabel={t("request.physicalExamination.rejectReasonLabel")}
          maxLength={150}
          rows={3}
          value={rejectReason.reason || ""}
          name={nameof<typeof rejectReason>("reason")}
          onChange={(e) => handleChange(e, setRejectReason)}
          required
        />
        <div className={styles.confirmButtonsContainer}>
          <div>
            <Button outline onClick={handleToggleRejectModalOpen}>
              {t("common.cancel")}
            </Button>
          </div>
          <div>
            <Button
              disabled={
                rejectReason.reason == null || rejectReason.reason.length === 0
              }
              onClick={handleRejectConfirmed}
            >
              {t("request.physicalExamination.rejectConfirm")}
            </Button>
          </div>
        </div>
      </Modal>
      <Modal
        type="message"
        open={rejectSuccessModalOpen}
        confirmText={t("common.close")}
        onConfirm={handleRejectSuccessModalClose}
        onClose={handleRejectSuccessModalClose}
      >
        <div className={styles.modalMessageContent}>
          <Typography variant="largeBold">
            {t("request.physicalExamination.successfullyRejected")}
          </Typography>
        </div>
      </Modal>
    </>
  );
};
